import { authClient } from "@/api/auth-client";
import { trpc } from "@/api/trpc";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormError } from "@/shared/components/form-error";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "@tanstack/react-router";
import { type RegisterInput, registerSchema } from "@template/shared";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { PasswordInput } from "./password-input";

/** Registration form with name, email, and password fields. */
export function RegisterForm() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
  });

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const createProfile = trpc.auth.createProfile.useMutation();

  const onSubmit = handleSubmit(async (data) => {
    setIsLoading(true);
    setServerError(null);

    const result = await authClient.signUp.email({
      email: data.email,
      password: data.password,
      name: data.name,
    });

    if (result.error) {
      setServerError(result.error.message ?? "Failed to create account");
      setIsLoading(false);
      return;
    }

    try {
      await createProfile.mutateAsync({ name: data.name });
    } catch {
      // Profile creation failed but account exists — continue to dashboard
      // The profile setup page will handle it
    }

    navigate({ to: "/dashboard" });
  });

  return (
    <Card className="w-full max-w-md" data-testid="auth-register-card">
      <CardHeader className="text-center">
        <CardTitle>Create Account</CardTitle>
        <CardDescription>Sign up for a new account</CardDescription>
      </CardHeader>
      <CardContent>
        <FormError message={serverError ?? undefined} testId="auth-register-error" />

        <form onSubmit={onSubmit} className="space-y-4" data-testid="auth-register-form">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              type="text"
              placeholder="John Doe"
              autoFocus
              aria-describedby={errors.name ? "name-error" : undefined}
              data-testid="auth-register-name-input"
              {...register("name")}
            />
            {errors.name && (
              <p
                id="name-error"
                className="text-sm text-destructive"
                data-testid="auth-register-name-error"
              >
                {errors.name.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              aria-describedby={errors.email ? "email-error" : undefined}
              data-testid="auth-register-email-input"
              {...register("email")}
            />
            {errors.email && (
              <p
                id="email-error"
                className="text-sm text-destructive"
                data-testid="auth-register-email-error"
              >
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password (min. 12 characters)</Label>
            <PasswordInput
              id="password"
              aria-describedby={errors.password ? "password-error" : undefined}
              data-testid="auth-register-password-input"
              {...register("password")}
            />
            {errors.password && (
              <p
                id="password-error"
                className="text-sm text-destructive"
                data-testid="auth-register-password-error"
              >
                {errors.password.message}
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
            data-testid="auth-register-submit-button"
          >
            {isLoading ? "Creating account..." : "Sign Up"}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="justify-center">
        <p className="text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-medium text-foreground hover:underline"
            data-testid="auth-register-login-link"
          >
            Sign In
          </Link>
        </p>
      </CardFooter>
    </Card>
  );
}
